const pad = (value: number): string => String(value).padStart(2, '0')

const DAY_MS = 86400000

export const toISODate = (date: Date): string => {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export const todayISO = (): string => toISODate(new Date())

export const isISODate = (value: string | null | undefined): value is string => {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
}

export const parseISODate = (value: string): Date => {
    const [year, month, day] = value.split('-').map(Number)

    return new Date(year, (month || 1) - 1, day || 1)
}

export const addDays = (value: string, days: number): string => {
    const date = parseISODate(value)
    date.setDate(date.getDate() + days)

    return toISODate(date)
}

export const addMonths = (value: string, months: number): string => {
    const date = parseISODate(value)
    const day = date.getDate()

    date.setDate(1)
    date.setMonth(date.getMonth() + months)

    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
    date.setDate(Math.min(day, lastDay))

    return toISODate(date)
}

/** Whole days from `to` until `from` (positive when `from` is later). */
export const diffDays = (from: string, to: string): number => {
    return Math.round((parseISODate(from).getTime() - parseISODate(to).getTime()) / DAY_MS)
}

export const dateFromTimestamp = (iso: string): string => toISODate(new Date(iso))

export const isSameMonth = (value: string, reference: string): boolean => value.slice(0, 7) === reference.slice(0, 7)

export const yearsBetween = (from: string, to: string): number => {
    const start = parseISODate(from)
    const end = parseISODate(to)
    let years = end.getFullYear() - start.getFullYear()

    if (end.getMonth() < start.getMonth() || (end.getMonth() === start.getMonth() && end.getDate() < start.getDate())) years -= 1

    return Math.max(0, years)
}

const shortFormat = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
const dayMonthFormat = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' })
const weekdayFormat = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
const longFormat = new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const fullFormat = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })

export const formatDate = (value: string | null | undefined): string => {
    if (!isISODate(value)) return '—'

    return shortFormat.format(parseISODate(value))
}

export const formatDayMonth = (value: string | null | undefined): string => {
    if (!isISODate(value)) return '—'

    return dayMonthFormat.format(parseISODate(value))
}

export const formatWeekday = (value: string | null | undefined): string => {
    if (!isISODate(value)) return '—'

    return weekdayFormat.format(parseISODate(value))
}

export const formatDateLong = (value: string | null | undefined): string => {
    if (!isISODate(value)) return ''

    return longFormat.format(parseISODate(value))
}

export const formatDateFull = (value: string | null | undefined): string => {
    if (!isISODate(value)) return ''

    return fullFormat.format(parseISODate(value))
}

export const formatTime = (value: string | null | undefined): string => {
    if (!value || !/^\d{1,2}:\d{2}/.test(value)) return ''

    const [hourText, minuteText] = value.split(':')
    const hour = Number(hourText)
    const suffix = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 === 0 ? 12 : hour % 12

    return `${displayHour}:${minuteText.slice(0, 2)} ${suffix}`
}

export const timeFromTimestamp = (iso: string): string => {
    const date = new Date(iso)

    return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export const formatTimestamp = (iso: string | null | undefined): string => {
    if (!iso) return '—'

    const date = new Date(iso)

    return `${shortFormat.format(date)}, ${formatTime(timeFromTimestamp(iso))}`
}

/** "Today", "Tomorrow", "In 5 days", "3 days ago". */
export const relativeDay = (value: string, today: string): string => {
    const diff = diffDays(value, today)

    if (diff === 0) return 'Today'
    if (diff === 1) return 'Tomorrow'
    if (diff === -1) return 'Yesterday'
    if (diff > 1 && diff < 45) return `In ${diff} days`
    if (diff < -1 && diff > -45) return `${Math.abs(diff)} days ago`
    if (diff > 0) return `In ${Math.round(diff / 30)} months`

    return `${Math.round(Math.abs(diff) / 30)} months ago`
}

export const overdueLabel = (value: string, today: string): string => {
    const diff = diffDays(today, value)

    if (diff <= 0) return ''
    if (diff === 1) return '1 day overdue'

    return `${diff} days overdue`
}

export const greetingFor = (date: Date): string => {
    const hour = date.getHours()

    if (hour < 12) return 'Good morning'
    if (hour < 17) return 'Good afternoon'

    return 'Good evening'
}

export interface QuickDateOption {
    label: string
    days?: number
    months?: number
}

export const QUICK_FOLLOW_UP_OPTIONS: QuickDateOption[] = [
    { label: 'Tomorrow', days: 1 },
    { label: '3 days', days: 3 },
    { label: '1 week', days: 7 },
    { label: '2 weeks', days: 14 },
    { label: '1 month', months: 1 },
    { label: '3 months', months: 3 },
    { label: '6 months', months: 6 }
]

export const applyQuickDate = (base: string, option: QuickDateOption): string => {
    if (option.months) return addMonths(base, option.months)

    return addDays(base, option.days ?? 0)
}
